import React, { useMemo, useState } from 'react';
import { IOrder } from '../../interfaces/order';
import { toFixedThree } from '../../utility/helper';
import {
    RefundLine,
    RefundQuote,
    RefundSelection,
    buildRefundContext,
    clampReturnQuantity,
    getOrderItemKindBadgeClass,
    getOrderItemKindLabel,
    quoteRefund,
} from '../../utility/refund';
import Button from '../../components/UI/Buttons/Button';

interface RefundRequestCardProps {
    order: IOrder;
    loading: boolean;
    onAccept: (quote: RefundQuote) => void;
    onReject: () => void;
}

/** Status the order sits in while a customer refund request awaits a decision. */
const PENDING_REFUND_STATUS = 'CLIENT UPDATED ORDER';

/**
 * Order Refund Request card — lists the items the customer asked to return with
 * their full breakdown (modifiers, combo options, Meatery Box contents), lets
 * the admin pick how many units of each go back, and prices the refund live
 * from the shared refund engine.
 */
const RefundRequestCard: React.FC<RefundRequestCardProps> = ({ order, loading, onAccept, onReject }) => {
    const [selection, setSelection] = useState<RefundSelection>({});
    const [errorMessage, setErrorMessage] = useState<string>('');

    const context = useMemo(() => buildRefundContext(order), [order]);
    const quote = useMemo(() => quoteRefund(context, selection), [context, selection]);

    const refundInfo = order.refundInfo;
    const isEditable = order.status === PENDING_REFUND_STATUS;
    const requestedLines = useMemo(() => context.lines.filter((l) => l.isRequested), [context]);
    const selectableLines = useMemo(
        () => requestedLines.filter((l) => l.returnableQuantity > 0),
        [requestedLines],
    );
    const allSelected =
        selectableLines.length > 0 &&
        selectableLines.every((l) => (selection[l.itemId] ?? 0) >= l.returnableQuantity);

    if (!refundInfo) return null;

    const setLineQuantity = (line: RefundLine, quantity: number) => {
        setErrorMessage('');
        setSelection((prev) => {
            const next = { ...prev };
            const clamped = clampReturnQuantity(line, quantity);
            if (clamped <= 0) delete next[line.itemId];
            else next[line.itemId] = clamped;
            return next;
        });
    };

    const toggleLine = (line: RefundLine) => {
        const isSelected = (selection[line.itemId] ?? 0) > 0;
        setLineQuantity(line, isSelected ? 0 : line.returnableQuantity);
    };

    const toggleAll = () => {
        setErrorMessage('');
        if (allSelected) return setSelection({});
        const next: RefundSelection = {};
        selectableLines.forEach((line) => { next[line.itemId] = line.returnableQuantity; });
        setSelection(next);
    };

    const handleAccept = () => {
        if (quote.itemCount === 0) {
            return setErrorMessage('Please select at least one item to refund.');
        }
        if (quote.total <= 0) {
            return setErrorMessage('The calculated refund amount is zero. Please review the selected items.');
        }
        setErrorMessage('');
        onAccept(quote);
    };

    /** Prior refunds — the ledger when the API returns one, else the single legacy amount. */
    const refundHistory = refundInfo.refundHistory?.length
        ? refundInfo.refundHistory
        : (context.alreadyRefunded > 0 ? [{ amount: context.alreadyRefunded }] : []);

    const summaryRow = (label: string, value: string, className = '') => (
        <div className="d-flex justify-content-between align-items-center mb-1">
            <span className={className}>{label}</span>
            <span className={`text-nowrap ${className}`}>{value}</span>
        </div>
    );

    return (
        <div className="app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4">
            <div className="app-card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <h5 className="fw-bold mb-3">Order Refund Request</h5>
                    {order.status === 'REFUNDED' && (
                        <span className="badge text-bg-success d-flex align-items-center gap-2 text-white">
                            Refund Request Accepted
                        </span>
                    )}
                    {order.status === 'REFUNDED REJECTED' && (
                        <span className="badge text-bg-danger d-flex align-items-center gap-2 text-white">
                            Refund Request Rejected
                        </span>
                    )}
                </div>

                {order.status === 'REFUNDED' && (
                    <div className="mb-3">
                        <strong>Refund Info:</strong>
                        <p className="mb-0">
                            {`An amount of ${toFixedThree(refundInfo.refundAmount ?? 0)} has been transferred to the user's Meatery wallet.`}
                        </p>
                    </div>
                )}

                <div className="row">
                    <div className="col-sm-12">
                        <div className="mb-3">
                            <strong>Refund Reason:</strong>
                            <p className="mb-0">{refundInfo.message}</p>
                        </div>
                    </div>
                    <div className="col-sm-12">
                        {refundInfo.rejectReason && (
                            <div className="mb-3">
                                <strong>Reject Reason:</strong>
                                <p className="mb-0">{refundInfo.rejectReason} </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Requested items, with the breakdown each product type needs */}
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-1">
                        <strong>Items:</strong>
                        {isEditable && selectableLines.length > 1 && (
                            <button type="button" className="btn btn-link p-0 small" onClick={toggleAll}>
                                {allSelected ? 'Clear selection' : 'Select all items'}
                            </button>
                        )}
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover mb-0" style={{ minWidth: '760px' }}>
                            <thead>
                                <tr>
                                    {isEditable && <th scope="col" style={{ width: '40px' }}></th>}
                                    <th scope="col">Product</th>
                                    <th scope="col">Details</th>
                                    <th scope="col" className="text-center text-nowrap" style={{ width: '90px' }}>Ordered</th>
                                    <th scope="col" className="text-center text-nowrap" style={{ width: '140px' }}>
                                        {isEditable ? 'Return Qty' : 'Returned'}
                                    </th>
                                    <th scope="col" className="text-end text-nowrap" style={{ width: '110px' }}>Unit Price</th>
                                    <th scope="col" className="text-end text-nowrap" style={{ width: '120px' }}>Refund</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requestedLines.map((line) => {
                                    const returnQty = selection[line.itemId] ?? 0;
                                    const isSelected = returnQty > 0;
                                    const lineQuote = quote.lines.find((l) => l.line.itemId === line.itemId);
                                    const isExhausted = line.returnableQuantity <= 0;

                                    return (
                                        <React.Fragment key={line.itemId}>
                                            <tr className={isSelected ? 'table-active' : ''}>
                                                {isEditable && (
                                                    <td className="align-middle">
                                                        <input
                                                            id={`refund-${line.itemId}`}
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={isSelected}
                                                            disabled={isExhausted}
                                                            onChange={() => toggleLine(line)}
                                                        />
                                                    </td>
                                                )}
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0 me-3">
                                                            <img
                                                                src={line.image || ''}
                                                                alt={line.name}
                                                                className="img-thumbnail"
                                                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <label className="fw-bold mb-1 text-break" htmlFor={`refund-${line.itemId}`}>
                                                                {line.name}
                                                            </label>
                                                            <div className="d-flex flex-wrap gap-1">
                                                                <span className={`badge ${getOrderItemKindBadgeClass(line.kind)}`}>
                                                                    {getOrderItemKindLabel(line.kind)}
                                                                </span>
                                                                {line.refundedQuantity > 0 && (
                                                                    <span className="badge bg-success bg-opacity-10 text-success">
                                                                        {line.refundedQuantity} already refunded
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.85rem' }}>
                                                        <div className="mb-1">
                                                            <strong>Base:</strong> BHD {toFixedThree(line.unitBasePrice)}
                                                        </div>
                                                        {line.unitModifierTotal > 0 && (
                                                            <div className="mb-1">
                                                                <strong>Options:</strong> {line.modifiers.length} selected · +BHD {toFixedThree(line.unitModifierTotal)}
                                                            </div>
                                                        )}
                                                        {line.kind === 'BOX' && (
                                                            <div className="mb-1">
                                                                <strong>Bundle:</strong> {line.components.length} items
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="align-middle text-center">
                                                    <span className="fw-medium">{line.quantity}</span>
                                                </td>
                                                <td className="align-middle text-center">
                                                    {isEditable ? (
                                                        isExhausted ? (
                                                            <span className="badge bg-secondary bg-opacity-10 text-secondary">
                                                                Fully refunded
                                                            </span>
                                                        ) : (
                                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary px-2 py-0"
                                                                    disabled={returnQty <= 0}
                                                                    onClick={() => setLineQuantity(line, returnQty - 1)}
                                                                >
                                                                    −
                                                                </button>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-center px-1"
                                                                    style={{ width: '58px' }}
                                                                    min={0}
                                                                    max={line.returnableQuantity}
                                                                    value={returnQty}
                                                                    onChange={(e) => setLineQuantity(line, Number(e.target.value))}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary px-2 py-0"
                                                                    disabled={returnQty >= line.returnableQuantity}
                                                                    onClick={() => setLineQuantity(line, returnQty + 1)}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        )
                                                    ) : (
                                                        <span className="fw-medium">{line.refundedQuantity || '-'}</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-end text-nowrap">
                                                    BHD {toFixedThree(line.unitPrice)}
                                                </td>
                                                <td className="align-middle text-end text-nowrap">
                                                    <span className={lineQuote ? 'fw-bold' : 'text-muted'}>
                                                        BHD {toFixedThree(lineQuote?.total ?? 0)}
                                                    </span>
                                                </td>
                                            </tr>

                                            {/* Selected modifiers / combo options */}
                                            {line.modifiers.map((mod, mIdx) => (
                                                <tr key={`${line.itemId}-mod-${mod.optionId || mIdx}`} className="table-light">
                                                    {isEditable && <td></td>}
                                                    <td>
                                                        <div className="d-flex align-items-center ms-4">
                                                            <div className="flex-shrink-0 me-2">
                                                                {mod.image ? (
                                                                    <img
                                                                        src={mod.image}
                                                                        alt={mod.name}
                                                                        className="img-thumbnail"
                                                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center bg-light border rounded"
                                                                        style={{ width: '40px', height: '40px' }}
                                                                    >
                                                                        <i className="bi bi-plus-circle text-muted" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="fw-medium text-break" style={{ fontSize: '0.9rem' }}>
                                                                    {mod.name}
                                                                </div>
                                                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                    {mod.groupName || 'Selected option'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: '0.85rem' }}>
                                                            <strong>Price:</strong>{' '}
                                                            {mod.price === 0
                                                                ? <span className="badge bg-success">Free</span>
                                                                : <>BHD {toFixedThree(mod.price)}</>}
                                                        </div>
                                                        <div style={{ fontSize: '0.85rem' }}>
                                                            <strong>weight:</strong>{' '}
                                                            {mod?.weight ?? 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="small">×{mod.quantity}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="small text-muted">
                                                            {returnQty > 0 ? `×${mod.quantity * returnQty}` : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="align-middle text-end text-nowrap">
                                                        <span className="small">BHD {toFixedThree(mod.total)}</span>
                                                    </td>
                                                    <td className="align-middle text-end text-nowrap">
                                                        <span className="small text-muted">
                                                            BHD {toFixedThree(mod.total * returnQty)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}

                                            {/* Meatery Box contents — refunded with the box, never on their own */}
                                            {line.components.map((component) => (
                                                <tr key={`${line.itemId}-box-${component.id}`} className="table-light">
                                                    {isEditable && <td></td>}
                                                    <td>
                                                        <div className="d-flex align-items-center ms-4">
                                                            <div className="flex-shrink-0 me-2">
                                                                <img
                                                                    src={component.image || ''}
                                                                    alt={component.name}
                                                                    className="img-thumbnail"
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                />
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="fw-medium text-break" style={{ fontSize: '0.9rem' }}>
                                                                    {component.name}
                                                                </div>
                                                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                    Included in box
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: '0.85rem' }}>
                                                            <div><strong>Qty in box:</strong> {component.quantity}</div>
                                                            {component.weight && (
                                                                <div><strong>Weight:</strong> {component.weight}</div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="small">×{component.quantity}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="small text-muted">
                                                            {returnQty > 0 ? `×${component.quantity * returnQty}` : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="text-end text-nowrap">
                                                        <span className="small text-muted">Included</span>
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                                {requestedLines.length === 0 && (
                                    <tr>
                                        <td colSpan={isEditable ? 7 : 6} className="text-center text-muted">
                                            No refundable items found for this request.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {errorMessage && <div className="text-danger mt-2">{errorMessage}</div>}
                </div>

                <div className="row">
                    {/* Images */}
                    <div className="col-lg-7 col-12">
                        <div className="mb-3">
                            <div className="mb-1">
                                <strong>Images:</strong>
                            </div>
                            <div className="d-flex flex-wrap">
                                {refundInfo.images.map((src, index) => (
                                    <img
                                        key={index}
                                        src={src}
                                        alt={`Refund image ${index + 1}`}
                                        className="img-thumbnail me-2 mb-2"
                                        style={{ width: '100px', height: 'auto' }}
                                    />
                                ))}
                                {refundInfo.images.length === 0 && <span className="text-muted">No images attached.</span>}
                            </div>
                        </div>

                        {refundHistory.length > 0 && (
                            <div className="mb-3">
                                <div className="mb-1">
                                    <strong>Refund History:</strong>
                                </div>
                                <ul className="list-group">
                                    {refundHistory.map((entry, index) => (
                                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center gap-2 flex-wrap">
                                            <span>
                                                BHD {toFixedThree(entry.amount ?? 0)}
                                                {entry.items?.length ? ` · ${entry.items.length} item(s)` : ''}
                                            </span>
                                            <span className="text-muted small">{entry.date || entry.note || 'Refunded to wallet'}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Live refund summary */}
                    <div className="col-lg-5 col-12">
                        <div className="border rounded p-3">
                            <h6 className="fw-bold mb-3">Refund Summary</h6>
                            {summaryRow('Items subtotal', `BHD ${toFixedThree(quote.subtotal - quote.modifiersSubtotal)}`)}
                            {quote.modifiersSubtotal > 0 &&
                                summaryRow('Options / add-ons', `BHD ${toFixedThree(quote.modifiersSubtotal)}`)}
                            {summaryRow('Returned subtotal', `BHD ${toFixedThree(quote.subtotal)}`, 'fw-medium')}
                            {quote.discount > 0 &&
                                summaryRow('Discount', `− BHD ${toFixedThree(quote.discount)}`, 'text-success')}
                            {quote.tax > 0 &&
                                summaryRow(`VAT (${context.taxRate}%)`, `BHD ${toFixedThree(quote.tax)}`)}
                            {quote.deliveryFees > 0 &&
                                summaryRow('Delivery fees', `BHD ${toFixedThree(quote.deliveryFees)}`)}
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between align-items-center">
                                <strong>Total refund</strong>
                                <strong className="text-nowrap">BHD {toFixedThree(quote.total)}</strong>
                            </div>
                            <hr className="my-2" />
                            {quote.alreadyRefunded > 0 &&
                                summaryRow('Previously refunded', `BHD ${toFixedThree(quote.alreadyRefunded)}`, 'text-muted small')}
                            {summaryRow('Refundable balance', `BHD ${toFixedThree(quote.refundableBalance)}`, 'text-muted small')}
                            {summaryRow('Balance after refund', `BHD ${toFixedThree(quote.remainingBalance)}`, 'text-muted small')}

                            {quote.isFullReturn && (
                                <div className="badge bg-primary bg-opacity-10 text-primary mt-2">Full order return</div>
                            )}
                            {quote.warnings.map((warning, index) => (
                                <div key={index} className="text-warning small mt-2">
                                    <i className="bi bi-exclamation-triangle me-1" />{warning}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {isEditable && (
                    <div className="mt-3 pt-3 border-top d-flex flex-wrap justify-content-end gap-2">
                        <Button loading={loading} className="app-btn-primary" type="button" text="Accept" onClick={handleAccept} />
                        <Button loading={loading} className="btn-danger" type="button" text="Reject" onClick={onReject} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default RefundRequestCard;
